import { fetchBrowsePlaces, getPlacesCapabilities } from './placesBrowseClient';
import { knownCityCoords, primaryCityName } from './knownCityCoordinates';
import {
  AI_SUGGESTION_RADIUS_MILES,
  namesAreNearDuplicate,
  tripLocationNames,
} from './partnerTripAiContext';
import { getTripLocationsArray } from './tripUtils';
import { milesBetween } from './exploreMapUtils';

export const BROWSE_CATEGORY_BY_PICK = {
  hotels: { google: 'lodging', osm: 'hotel' },
  restaurants: { google: 'restaurant', osm: 'restaurant' },
  locations: { google: 'tourist_attraction', osm: 'attraction' },
  activities: { google: 'tourist_attraction', osm: 'attraction' }
};

const PRICE_LEVEL_MARKS = {
  PRICE_LEVEL_FREE: 0,
  PRICE_LEVEL_INEXPENSIVE: 1,
  PRICE_LEVEL_MODERATE: 2,
  PRICE_LEVEL_EXPENSIVE: 3,
  PRICE_LEVEL_VERY_EXPENSIVE: 4,
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4
};

export function priceLevelMarks(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' && /^\$+$/.test(value)) return value.length;
  const marks = PRICE_LEVEL_MARKS[value];
  return Number.isInteger(marks) ? marks : null;
}

export function priceLevelLabel(value) {
  const marks = priceLevelMarks(value);
  if (marks == null) return '';
  if (marks === 0) return 'Free';
  return '$'.repeat(marks);
}

export function formatNearbyCatalog(byCategory = {}, limit = 8) {
  return ['hotels', 'restaurants', 'locations']
    .map((category) => {
      const rows = dedupePlaces(byCategory[category] || []).slice(0, limit);
      if (!rows.length) return '';
      const list = rows.map((place) => {
        const bits = [
          place.name,
          place.city && `(${place.city})`,
          place.rating && String(place.rating)
        ].filter(Boolean);
        return bits.join(' ');
      }).join('; ');
      return `${category}: ${list}`;
    })
    .filter(Boolean)
    .join('\n');
}

export function matchNearbyPlace(item, places = []) {
  if (!item?.name) return null;
  return places.find((place) => namesAreNearDuplicate(item.name, place.name)) || null;
}

export function attachNearbyPlace(item, places = []) {
  const match = matchNearbyPlace(item, places);
  if (!match) return item;
  const rating = item.rating || (match.rating != null ? `${match.rating}/5` : '');
  return {
    ...item,
    city: item.city || match.city || '',
    address: item.address || match.address || '',
    rating: rating || item.rating,
    starRating: item.starRating || match.starRating || null,
    priceLevel: match.priceLevel ?? item.priceLevel,
    photoName: match.photoName || item.photoName || null,
    placeId: match.placeId || item.placeId || null,
    userRatingCount: match.userRatingCount || item.userRatingCount || null,
    website: item.website || match.website || '',
    cuisine: item.cuisine || match.cuisine || '',
    nearbyMatch: true,
    source: match.source || item.source,
    lat: item.lat ?? match.lat ?? match.geometry?.lat ?? null,
    lng: item.lng ?? match.lng ?? match.geometry?.lng ?? null,
    geometry: item.geometry || match.geometry || null
  };
}

export function suggestionCoords(item = {}) {
  const lat = Number(
    item.lat
    ?? item.latitude
    ?? item.coordinates?.lat
    ?? item.geometry?.lat
  );
  const lng = Number(
    item.lng
    ?? item.longitude
    ?? item.coordinates?.lng
    ?? item.geometry?.lng
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function placeNameToken(name = '') {
  return primaryCityName(name).toLowerCase();
}

export function collectTripAnchorPoints({ trip, extraByName = {} } = {}) {
  const points = [];
  const seen = new Set();
  const add = (name, source) => {
    const coords = suggestionCoords(source || {});
    if (!coords) return;
    const key = `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({ name: String(name || '').trim(), ...coords });
  };

  getTripLocationsArray(trip).forEach((location) => {
    add(location.name, location.coordinates || location);
  });
  Object.entries(extraByName || {}).forEach(([name, coords]) => {
    add(name, coords);
  });
  tripLocationNames(trip).forEach((name) => {
    const known = knownCityCoords(name);
    if (known) add(name, known);
  });
  return points;
}

export function isWithinTripRadius(point, anchors, miles = AI_SUGGESTION_RADIUS_MILES) {
  const coords = suggestionCoords(point);
  if (!coords || !anchors?.length) return !anchors?.length;
  return anchors.some((anchor) => (
    milesBetween(coords.lat, coords.lng, anchor.lat, anchor.lng) <= miles
  ));
}

function nameMatchesTripAnchor(name, anchors) {
  const token = placeNameToken(name);
  if (!token) return false;
  return anchors.some((anchor) => {
    const anchorToken = placeNameToken(anchor.name);
    return Boolean(anchorToken && (token.includes(anchorToken) || anchorToken.includes(token)));
  });
}

export function pickIsWithinTripRadius(pick, anchors, miles = AI_SUGGESTION_RADIUS_MILES) {
  if (!anchors?.length) return true;
  const coords = suggestionCoords(pick?.item) || suggestionCoords(pick);
  if (coords) return isWithinTripRadius(coords, anchors, miles);
  const city = pick?.city || pick?.item?.city || pick?.item?.location || '';
  const known = knownCityCoords(city) || knownCityCoords(pick?.label);
  if (known) return isWithinTripRadius(known, anchors, miles);
  // Same trip-city name without coords: allow. Anything else is outside the radius.
  if (nameMatchesTripAnchor(city, anchors) || nameMatchesTripAnchor(pick?.label, anchors)) {
    return true;
  }
  return false;
}

export function filterPicksNearTripAnchors(picks = [], anchors = [], miles = AI_SUGGESTION_RADIUS_MILES) {
  if (!anchors.length) return picks;
  return picks.filter((pick) => pickIsWithinTripRadius(pick, anchors, miles));
}

export function filterPlacesNearTripAnchors(places = [], anchors = [], miles = AI_SUGGESTION_RADIUS_MILES) {
  if (!anchors.length) return places;
  return places.filter((place) => {
    const coords = suggestionCoords(place);
    if (coords) return isWithinTripRadius(coords, anchors, miles);
    const known = knownCityCoords(place.city || place.name);
    if (known) return isWithinTripRadius(known, anchors, miles);
    return nameMatchesTripAnchor(place.city || place.name, anchors);
  });
}

export function filterNearbyByCategory(byCategory = {}, anchors = [], miles = AI_SUGGESTION_RADIUS_MILES) {
  if (!anchors.length) return byCategory;
  return {
    hotels: filterPlacesNearTripAnchors(byCategory.hotels, anchors, miles),
    restaurants: filterPlacesNearTripAnchors(byCategory.restaurants, anchors, miles),
    locations: filterPlacesNearTripAnchors(byCategory.locations, anchors, miles),
    activities: filterPlacesNearTripAnchors(byCategory.activities, anchors, miles)
  };
}

export function suggestionGeocodeQuery(pick) {
  const item = pick?.item || {};
  const name = String(pick?.label || item.name || '').trim();
  const city = String(item.city || pick?.city || '').trim();
  if (name && city) return `${name}, ${city}`;
  return city || name;
}

export function picksToMapPois(picks = [], extraCoords = {}) {
  return picks.map((pick) => {
    const coords = suggestionCoords(pick.item) || extraCoords[pick.key] || null;
    if (!coords) return null;
    return {
      key: pick.key,
      name: pick.label,
      category: pick.category,
      city: pick.city || pick.item?.city || '',
      rating: pick.item?.rating || '',
      lat: coords.lat,
      lng: coords.lng
    };
  }).filter(Boolean);
}

export function attachNearbyPlacesToPicks(picks = [], byCategory = {}) {
  return picks.map((pick) => {
    const pool = [
      ...(byCategory[pick.category] || []),
      ...(pick.category === 'activities' ? (byCategory.locations || []) : [])
    ];
    const item = attachNearbyPlace(pick.item, pool);
    return {
      ...pick,
      city: pick.city || item.city || '',
      item
    };
  });
}

function dedupePlaces(places) {
  const seen = new Set();
  return places.filter((place) => {
    const key = String(place?.name || '').toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function loadNearbyPlacesForTrip({
  tripId,
  destinations = [],
  requestConfig
} = {}) {
  const empty = { hotels: [], restaurants: [], locations: [], activities: [] };
  if (!tripId || !destinations.length) {
    return { google: false, byCategory: empty };
  }

  let google = false;
  try {
    const caps = await getPlacesCapabilities(tripId, requestConfig);
    google = Boolean(caps?.hasGooglePlacesBrowse);
  } catch {
    google = false;
  }

  const byCategory = { hotels: [], restaurants: [], locations: [], activities: [] };
  const jobs = destinations.flatMap((location) => (
    ['hotels', 'restaurants', 'locations'].map(async (bucket) => {
      const category = BROWSE_CATEGORY_BY_PICK[bucket][google ? 'google' : 'osm'];
      try {
        const result = await fetchBrowsePlaces({
          tripId,
          location,
          category,
          google,
          requestConfig
        });
        const places = (result.places || []).map((place) => ({
          ...place,
          city: location,
          browseCategory: bucket
        }));
        byCategory[bucket].push(...places);
        if (bucket === 'locations') byCategory.activities.push(...places);
      } catch {
        // Browse is optional; AI still runs without it.
      }
    })
  ));
  await Promise.all(jobs);
  return { google, byCategory };
}
